// Shared score → colour/label mapping used by the practice screens (Manual,
// Automatic) so the thresholds and palette live in ONE place.

// Matches MASTERED_SCORE on the server, which is what the dashboard's
// Mastered count is built from. Change both or neither.
export const MASTERED_SCORE = 90;

// ── ONE colour scale for every score in the app (Leanne, 19 Oct 2026) ─────
// Four bands, used by every screen: the score circle, the phrase highlights,
// the word cards, the session summary, the Phrases page and the dashboard.
// Before this there were four different scales in different files, so the same
// score showed in different colours from one screen to the next.
//   80 to 100  emerald      Great, Excellent
//   60 to 79   soft green   Good, Fair (not lime: Leanne, 22 Oct 2026)
//   40 to 59   orange       Getting there
//   under 40   red          Keep practising
// A perfect 100 keeps its gold where a score is shown on its own.
export const scoreBand = (score) => (score == null ? null
    : score >= 80 ? 'green' : score >= 60 ? 'soft' : score >= 40 ? 'amber' : 'rose');

// Below green, a score has something to work on: the weak syllables of a word,
// the weak sounds inside "Aim for", the cards worth showing first.
export const NEEDS_WORK = 80;
export const needsWork = (score) => score != null && score < NEEDS_WORK;

// "Redo" on the session summary: the orange and red bands.
export const REDO_BELOW = 60;

// The new results layout and wording, switched on per account by the server
// (pronunciation_beta). Everyone has it since 19 Oct 2026.
let speechaceBands = false;
export const setSpeechaceBands = (on) => { speechaceBands = !!on; };
// The same switch, read by the cards for the other changes on trial with it.
export const pronunciationBeta = () => speechaceBands;

// Great and Fair are both green, so they differ in strength (Leanne, 22 Oct
// 2026): Great is a deeper emerald with a stronger tint, Fair a brighter green
// with a very light tint and a green outline.
const TEXT = { green: 'text-emerald-700', soft: 'text-green-500', amber: 'text-amber-600', rose: 'text-rose-600' };
const SOLID = { green: 'bg-gradient-to-br from-emerald-300 via-emerald-500 to-emerald-600', soft: 'bg-green-400',
                amber: 'bg-amber-500', rose: 'bg-rose-500' };
const SOFT = { green: 'border-emerald-300 bg-emerald-100', soft: 'border-green-200 bg-[#f7fef9]',
               amber: 'border-amber-200 bg-amber-50', rose: 'border-rose-200 bg-rose-50' };
const TINT = { green: 'bg-emerald-200 text-emerald-900', soft: 'bg-green-50 text-green-700 shadow-[inset_0_0_0_1.5px_#86efac]',
               amber: 'bg-amber-100 text-amber-900', rose: 'bg-rose-100 text-rose-900' };

// The score printed on a word's card: the same colour as the word in the phrase.
export const cardScoreText = (score) => (score == null ? 'text-stone-400' : TEXT[scoreBand(score)]);

// One scale for the label above a score and the line that explains it, so
// they can never disagree (53 once read "Fair" above "Poor"). Speechace's
// rubric with a level added under 60 and no "poor" (Leanne, 7 Oct 2026).
const BANDS = [
    [90, 'Excellent!', 'Native or native-like.'],
    [80, 'Great!', 'Very good and clearly understandable.'],
    [70, 'Good!', 'Comprehensible, with one or two clear mistakes.'],
    [60, 'Fair', 'May be hard to understand, with several clear mistakes.'],
    [40, 'Getting there', 'Several sounds need work.'],
    [0, 'Keep practising', 'Many sounds need work.'],
];
const bandFor = (score) => BANDS.find(([from]) => score >= from) || BANDS[BANDS.length - 1];

// What a score means, under the overall score. Empty while the bands are off,
// so nothing new shows for everyone else.
export const scoreDescription = (score) => {
    if (!speechaceBands || score == null) return '';
    return bandFor(score)[2];
};

// The same for a word's card, which has no label above it: "Fair: may be
// hard to understand, with several clear mistakes."
export const wordScoreDescription = (score, soundCount = 0) => {
    if (!speechaceBands || score == null) return '';
    const [from, label, band] = bandFor(score);
    // Below 80 the band's own wording counts mistakes ("several sounds need
    // work"), which a card listing one sound contradicted. With the number of
    // sounds the card lists, it says exactly that many.
    if (from < 80 && soundCount > 0) {
        const sounds = soundCount === 1 ? 'one sound needs' : soundCount === 2 ? 'two sounds need' : 'some sounds need';
        const name = label.replace(/!$/, '');
        if (from >= 70) return `${name}: comprehensible, ${sounds} a little work.`;
        if (from >= 60) return `${name}: may be hard to understand, ${sounds} work.`;
        return `${name}: ${sounds} work. Give it a listen.`;
    }
    // "Give it another go" is about a recording, not a word: on a word's card
    // the next step is to hear it (Leanne, 7 Oct 2026).
    const text = band.replace('Give it another go.', 'Give it a listen.');
    return `${label.replace(/!$/, '')}: ${text.charAt(0).toLowerCase()}${text.slice(1)}`;
};

/**
 * Which attempts at which words have something to say about them: the set of
 * keys that own a tip card. A word is tinted in the phrase ONLY if it is in
 * here, whatever it scored, so the colour on the phrase and the cards beneath
 * it are the same list. A word with no feedback stays plain even when its
 * score is poor: there would be nothing underneath to explain the colour.
 */
// At most this many words are highlighted and carded per result, worst first,
// so a weak attempt is not a wall of colour. Tapping ANY word still shows its
// feedback: the server sends all of it, only the highlighting and the cards
// are capped, and both read this one list.
export const MAX_FEEDBACK_WORDS = 6;

export const feedbackKeys = ({ phoneTips = [], stressTips = [], issues = [], wordScores = [], missed = [] } = {}) => {
    // How bad each word is: its worst sound, or its own score if that is lower.
    // Ranked by sound, not word, so a missed liaison scored 25 inside a word
    // scored 84 is not the first thing cut.
    const worst = new Map();
    const note = (key, s) => worst.set(key, Math.min(worst.has(key) ? worst.get(key) : 101, s));
    for (const t of phoneTips || []) {
        if (normWordKey(t.word)) note(occKey(t.word, occOf(t)), t.score ?? 100);
    }
    for (const t of [...(stressTips || []), ...(issues || [])]) {
        if (normWordKey(t.word)) note(occKey(t.word, occOf(t)), 100);
    }
    // Words not said at all: marked too, worst of all, so the learner can see
    // which word went missing and tap it.
    for (const m of missed || []) {
        if (normWordKey(m.word)) note(occKey(m.word, occOf(m)), 0);
    }
    for (const w of wordScores || []) {
        const key = occKey(w.word, occOf(w));
        if (worst.has(key) && w.score != null) note(key, w.score);
    }
    // Beta accounts have no limit (Leanne, 17 Oct 2026): every word with
    // feedback is highlighted, so the colours always match the cards. Everyone
    // else still gets the worst few.
    const keys = new Set([...worst.keys()].sort((a, b) => worst.get(a) - worst.get(b))
        .slice(0, speechaceBands ? worst.size : MAX_FEEDBACK_WORDS));
    // Never one half of a labelled pair: "le (1st)" with no second reads as a
    // mistake, so a word that made the cut brings its other attempts with it.
    for (const key of [...keys]) {
        const base = key.split('#')[0];
        for (const k of worst.keys()) if (k.split('#')[0] === base) keys.add(k);
    }
    return keys;
};

// The tint behind a word in the phrase. One rule for every screen: the running
// phrase in Automatic, the result in Manual, and Quick Practice.
export const wordTint = (score) => (score == null ? null : TINT[scoreBand(score)]);

// The card for that same word, in the same band as its tint in the phrase.
export const tipCardTint = (score) => (score == null ? 'border-stone-200 bg-stone-50' : SOFT[scoreBand(score)]);

// ── Words, and which time they come up ──────────────────────────────────────
// A word said twice in one phrase is two attempts with two scores. Both the
// phrase (the colours) and the tips list (the cards) have to agree about that,
// or a word shows highlighted twice with only one card to explain it, which is
// exactly what happened while these lived in one component only.

// Lowercased, punctuation stripped, so "plaît," and "plaît" are one word.
export const normWordKey = (w) => (w || '').replace(/[^\p{L}\p{M}'\-]/gu, '').toLowerCase();

// Which time a word comes up, from 1. Results scored before this existed carry
// no occurrence and count as the first.
export const occOf = (t) => t?.occurrence || 1;
export const occKey = (word, occ) => `${normWordKey(word)}#${occ}`;
export const ordinal = (n) => ['1st', '2nd', '3rd'][n - 1] || `${n}th`;

// How many times each word appears in a phrase, for deciding whether a card
// needs the "(1st)" label at all.
export const countWords = (phrase) => {
    const counts = new Map();
    for (const raw of (phrase || '').split(/\s+/)) {
        const k = normWordKey(raw);
        if (k) counts.set(k, (counts.get(k) || 0) + 1);
    }
    return counts;
};

/**
 * Scores looked up by word AND which time it appears: `lookup(word, occ)`.
 *
 * `counter()` hands back a function that walks a phrase's words in order and
 * returns each one's occurrence number, so a caller rendering token by token
 * does not have to keep that tally itself.
 */
export const occScoreLookup = (wordScores) => {
    const byKey = new Map();
    for (const w of wordScores || []) {
        if (!normWordKey(w.word) || w.score == null) continue;
        const key = occKey(w.word, occOf(w));
        // Duplicate entries for the same attempt keep the LOWEST.
        if (!byKey.has(key) || byKey.get(key) > w.score) byKey.set(key, w.score);
    }
    return {
        size: byKey.size,
        get: (word, occ) => byKey.get(occKey(word, occ)),
        counter: () => {
            const seen = new Map();
            return (word) => {
                const k = normWordKey(word);
                if (!k) return 0;
                const n = (seen.get(k) || 0) + 1;
                seen.set(k, n);
                return n;
            };
        },
    };
};

export const getScoreColor = (score) => {
    if (score == null) return 'text-stone-400';
    if (score >= 100) return 'text-yellow-500';
    return TEXT[scoreBand(score)];
};

export const getScoreBg = (score) => {
    if (score >= 100) return 'bg-gradient-to-br from-yellow-300 to-amber-500';
    return SOLID[scoreBand(score)] || SOLID.rose;
};

// Soft tint + border for list rows and cards, in the same band as the score.
export const getScoreRowClasses = (score) => {
    if (score == null) return 'border-stone-200 bg-stone-50';
    if (score >= 100) return 'border-yellow-200 bg-yellow-50';
    return SOFT[scoreBand(score)];
};

// Same bands as the colours above, and as the rubric Gemini scores against, so
// the word on a score and the number behind it can never disagree. Excellent
// starts at MASTERED_SCORE: the word and the badge mean the same thing.
export const getScoreLabel = (score) => bandFor(score)[1];
